import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Globe, Users, Zap } from "lucide-react";

const HeroSection: React.FC = () => {
  // Animation variants with proper typing
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
  };

  // Skills/categories for the scrolling badges
  const skillsRow1 = [
    "Machine Learning",
    "Data Science",
    "Computer Science",
    "Mathematics",
    "Physics",
    "Engineering",
    "Medicine",
    "Law",
    "Economics",
    "Psychology",
    "Linguistics",
    "Philosophy",
  ];

  const skillsRow2 = [
    "Biology",
    "Chemistry",
    "History",
    "Literature",
    "Art & Design",
    "Music",
    "Education",
    "Business",
    "Finance",
    "Marketing",
    "Healthcare",
    "Research",
  ];

  const skillsRow3 = [
    "Natural Language",
    "Computer Vision",
    "Robotics",
    "Ethics",
    "Statistics",
    "Programming",
    "Writing",
    "Translation",
    "Analysis",
    "Quality Assurance",
    "Data Entry",
    "Annotation",
  ];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-gradient-to-b from-[#FAFAFA] to-white">
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(#8B52FF 1.5px, transparent 1.5px), linear-gradient(90deg, #8B52FF 1.5px, transparent 1.5px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Animated gradient orbs */}
      <motion.div
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-20 right-[20%] w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#8B52FF]/20 to-[#E05A6A]/10 blur-[100px]"
      />
      <motion.div
        animate={{
          scale: [1, 1.08, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute bottom-40 left-[10%] w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-[#E05A6A]/15 to-[#8B52FF]/10 blur-[80px]"
      />
      <motion.div
        animate={{
          scale: [1, 1.03, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-[#8B52FF]/5 to-[#E05A6A]/5 blur-[120px]"
      />

      {/* Floating elements for visual interest */}
      <motion.div
        animate={{
          y: [-10, 10, -10],
          rotate: [0, 5, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-32 left-[8%] hidden lg:block z-20"
      >
        <div className="w-16 h-16 rounded-2xl bg-white shadow-xl shadow-[#8B52FF]/10 border border-gray-100 flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-[#8B52FF]" />
        </div>
      </motion.div>

      <motion.div
        animate={{
          y: [10, -10, 10],
          rotate: [12, 8, 12],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
        className="absolute top-48 right-[8%] hidden lg:block z-20"
      >
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] shadow-xl shadow-[#8B52FF]/20 flex items-center justify-center rotate-12">
          <span className="text-3xl">🤖</span>
        </div>
      </motion.div>

      <motion.div
        animate={{
          y: [-8, 12, -8],
          rotate: [-12, -8, -12],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute bottom-[35%] left-[5%] hidden lg:block z-20"
      >
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E05A6A] to-[#8B52FF] shadow-xl shadow-[#E05A6A]/20 flex items-center justify-center -rotate-12">
          <span className="text-2xl">✨</span>
        </div>
      </motion.div>

      <motion.div
        animate={{
          y: [8, -8, 8],
          rotate: [0, -5, 0],
        }}
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
        className="absolute bottom-[40%] right-[6%] hidden lg:block z-20"
      >
        <div className="w-12 h-12 rounded-xl bg-white shadow-xl shadow-gray-200 border border-gray-100 flex items-center justify-center">
          <span className="text-xl">🌍</span>
        </div>
      </motion.div>

      {/* Additional floating elements */}
      <motion.div
        animate={{
          y: [-5, 15, -5],
          x: [-5, 5, -5],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        className="absolute top-[60%] left-[12%] hidden lg:block z-20"
      >
        <div className="w-10 h-10 rounded-lg bg-white shadow-lg shadow-[#8B52FF]/10 border border-gray-100 flex items-center justify-center">
          <span className="text-lg">💡</span>
        </div>
      </motion.div>

      <motion.div
        animate={{
          y: [5, -10, 5],
          rotate: [5, -5, 5],
        }}
        transition={{
          duration: 6.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.8,
        }}
        className="absolute top-[25%] right-[18%] hidden lg:block z-20"
      >
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B52FF]/80 to-[#A855F7] shadow-lg shadow-[#8B52FF]/15 flex items-center justify-center">
          <span className="text-lg">🧠</span>
        </div>
      </motion.div>

      {/* Animated lines connecting elements */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block"
        style={{ zIndex: 1 }}
      >
        <motion.path
          d="M100,200 Q300,100 500,300"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          fill="none"
          strokeDasharray="5,5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.3 }}
          transition={{ delay: 1.5, duration: 2 }}
        />
        <motion.path
          d="M900,150 Q700,250 500,200"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          fill="none"
          strokeDasharray="5,5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.3 }}
          transition={{ delay: 2, duration: 2 }}
        />
        <motion.path
          d="M150,400 Q350,350 550,450"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          fill="none"
          strokeDasharray="5,5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.2 }}
          transition={{ delay: 2.5, duration: 2 }}
        />
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8B52FF" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#E05A6A" stopOpacity="0.5" />
          </linearGradient>
        </defs>
      </svg>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 lg:pt-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8B52FF] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8B52FF]"></span>
              </span>
              <span className="text-sm font-medium text-gray-600">
                Join our 50K+ global community
              </span>
            </span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1]"
          >
            Get paid for your skills
            <br />
            <span className="relative inline-block mt-2">
              and shape{" "}
              <span className="relative">
                <span className="bg-gradient-to-r from-[#8B52FF] via-[#A855F7] to-[#E05A6A] bg-clip-text text-transparent">
                  the future of AI
                </span>
                {/* Decorative underline */}
                <motion.svg
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1, duration: 0.5 }}
                  className="absolute -bottom-2 left-0 w-full h-3"
                  viewBox="0 0 200 12"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 8C50 2 150 2 198 8"
                    stroke="url(#underlineGradient)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1.2, duration: 1, ease: "easeOut" }}
                  />
                  <defs>
                    <linearGradient
                      id="underlineGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#8B52FF" />
                      <stop offset="100%" stopColor="#E05A6A" />
                    </linearGradient>
                  </defs>
                </motion.svg>
              </span>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="mt-8 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
          >
            Work from anywhere, on your own schedule. Join our global community
            of experts contributing to{" "}
            <span className="text-gray-900 font-medium">ethical</span>,{" "}
            <span className="text-gray-900 font-medium">inclusive</span> AI
            development.
          </motion.p>

          {/* Scrolling Skills Badges */}
          <motion.div
            variants={itemVariants}
            className="mt-12 relative overflow-hidden py-4"
          >
            {/* Gradient masks for smooth fade effect */}
            <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-[#FAFAFA] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-[#FAFAFA] to-transparent z-10 pointer-events-none" />

            {/* Row 1 - moves left */}
            <div className="mb-3 overflow-hidden">
              <motion.div
                animate={{ x: ["0%", "-50%"] }}
                transition={{
                  x: {
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear",
                  },
                }}
                className="flex gap-3 w-fit"
              >
                {[...skillsRow1, ...skillsRow1].map((skill, index) => (
                  <span
                    key={`row1-${index}`}
                    className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap hover:border-[#8B52FF]/50 hover:text-[#8B52FF] transition-colors cursor-default shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Row 2 - moves right */}
            <div className="mb-3 overflow-hidden">
              <motion.div
                animate={{ x: ["-50%", "0%"] }}
                transition={{
                  x: {
                    duration: 35,
                    repeat: Infinity,
                    ease: "linear",
                  },
                }}
                className="flex gap-3 w-fit"
              >
                {[...skillsRow2, ...skillsRow2].map((skill, index) => (
                  <span
                    key={`row2-${index}`}
                    className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap hover:border-[#E05A6A]/50 hover:text-[#E05A6A] transition-colors cursor-default shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Row 3 - moves left (slower) */}
            <div className="overflow-hidden">
              <motion.div
                animate={{ x: ["0%", "-50%"] }}
                transition={{
                  x: {
                    duration: 40,
                    repeat: Infinity,
                    ease: "linear",
                  },
                }}
                className="flex gap-3 w-fit"
              >
                {[...skillsRow3, ...skillsRow3].map((skill, index) => (
                  <span
                    key={`row3-${index}`}
                    className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap hover:border-[#8B52FF]/50 hover:text-[#8B52FF] transition-colors cursor-default shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/careers">
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-8 py-4 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-xl text-white font-semibold text-lg shadow-xl shadow-[#8B52FF]/25 hover:shadow-2xl hover:shadow-[#8B52FF]/30 transition-all duration-300 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Explore Opportunities
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                    ease: "easeInOut",
                  }}
                />
              </motion.button>
            </Link>
          </motion.div>

          {/* Stats row */}
          <motion.div
            variants={itemVariants}
            className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto"
          >
            {[
              {
                icon: Globe,
                value: "10+",
                label: "Countries",
                color: "#8B52FF",
              },
              {
                icon: Users,
                value: "50K+",
                label: "Contributors",
                color: "#A855F7",
              },
              {
                icon: Zap,
                value: "$1M+",
                label: "Paid Out",
                color: "#E05A6A",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B52FF]/5 to-[#E05A6A]/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-4 sm:p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300">
                  <stat.icon
                    className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2"
                    style={{ color: stat.color }}
                  />
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                    {stat.value}
                  </div>
                  <div className="text-gray-500 text-sm sm:text-base font-medium">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative dots */}
      <div className="absolute top-32 left-[15%] w-2 h-2 rounded-full bg-[#8B52FF]/40 hidden lg:block" />
      <div className="absolute top-48 left-[20%] w-1.5 h-1.5 rounded-full bg-[#E05A6A]/50 hidden lg:block" />
      <div className="absolute top-40 right-[15%] w-3 h-3 rounded-full bg-[#8B52FF]/30 hidden lg:block" />
      <div className="absolute top-56 right-[22%] w-1.5 h-1.5 rounded-full bg-[#E05A6A]/40 hidden lg:block" />
      <div className="absolute bottom-[30%] left-[18%] w-2 h-2 rounded-full bg-[#A855F7]/30 hidden lg:block" />
      <div className="absolute bottom-[35%] right-[15%] w-2.5 h-2.5 rounded-full bg-[#8B52FF]/25 hidden lg:block" />

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-8 rounded-full border-2 border-gray-300 flex items-start justify-center p-1.5"
        >
          <motion.div className="w-1 h-1 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HeroSection;
